import { useCallback } from 'react'
import { useNavigate, type NavigateOptions, type To } from 'react-router-dom'
import { useChannelStore } from '@/store/channel'
import { toUrlLang } from '@/i18n/languages'
import { localizedPath } from '@/utils/localizedPath'

/** Current URL language prefix (`pt`, `en`, ...). */
export function useUrlLang(): string {
  const currentLanguageCode = useChannelStore((store) => store.currentLanguageCode)
  return toUrlLang(currentLanguageCode)
}

/** Returns `lp(path)` that prefixes internal paths with the current language. */
export function useLocalizedPath(): (path: string) => string {
  const lang = useUrlLang()
  return useCallback((path: string) => localizedPath(path, lang), [lang])
}

/**
 * Drop-in for `useNavigate()` that prefixes string targets with the current
 * language. Numeric deltas (`navigate(-1)`) and `To` objects pass through,
 * with `pathname` prefixed on the latter.
 */
export function useLocalizedNavigate() {
  const navigate = useNavigate()
  const lang = useUrlLang()

  return useCallback(
    (to: To | number, options?: NavigateOptions) => {
      if (typeof to === 'number') {
        navigate(to)
        return
      }
      if (typeof to === 'string') {
        navigate(localizedPath(to, lang), options)
        return
      }
      navigate(to.pathname ? { ...to, pathname: localizedPath(to.pathname, lang) } : to, options)
    },
    [navigate, lang]
  )
}
